import { useEffect } from "react";
import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import Directory from "./pages/Directory.jsx";
import Home from "./pages/Home.jsx";
import BatDau from "./pages/BatDau.jsx";
import About from "./pages/About.jsx";
import Workspace from "./pages/Workspace.jsx";
import Funding from "./pages/Funding.jsx";
import ToolPage from "./pages/ToolPage.jsx";
import Programs from "./pages/Programs.jsx";
import Glossary from "./pages/Glossary.jsx";
import GlossaryTerm from "./pages/GlossaryTerm.jsx";
import Legal from "./pages/Legal.jsx";
import NotFound from "./pages/NotFound.jsx";
import FundDetail from "./pages/FundDetail.jsx";
import Guide from "./pages/Guide.jsx";
import LoTrinh from "./pages/LoTrinh.jsx";
import Playbook from "./pages/Playbook.jsx";
import StorySubmit from "./pages/StorySubmit.jsx";
import { TemplateList, TemplateDetail } from "./pages/Templates.jsx";
import PlaybookArticle from "./pages/PlaybookArticle.jsx";
import PlaybookAdmin from "./pages/PlaybookAdmin.jsx";
import TemplateAdmin from "./pages/TemplateAdmin.jsx";
import ProgramAdmin from "./pages/ProgramAdmin.jsx";
import FundAdmin from "./pages/FundAdmin.jsx";
import StatsAdmin from "./pages/StatsAdmin.jsx";
import NhanXetDeck from "./pages/NhanXetDeck.jsx";
import NhanXetDeckAdmin from "./pages/NhanXetDeckAdmin.jsx";
import SpotlightAdmin from "./pages/SpotlightAdmin.jsx";
import VideoLibrary from "./pages/VideoLibrary.jsx";
import VideoAdmin from "./pages/VideoAdmin.jsx";
import DongYCookie from "./components/DongYCookie.jsx";
import DauTrang from "./components/DauTrang.jsx";
import { ghiLuotXemTrang, khoiDongDoLuong, track, DO_LUONG_BAT } from "./lib/track.js";
import { moLaiHopChon } from "./lib/consent.js";
import { metaFor } from "./lib/seo.js";
/**
 * Trang có dữ liệu người dùng tự nhập hoặc kết quả tính từ đó: che toàn bộ nội
 * dung trong bản ghi phiên của Microsoft Clarity (src/lib/clarity.js).
 */
const laTrangCanChe = (p) => p.startsWith("/cong-cu/") || p.startsWith("/ban-chuan-bi")
  || p.startsWith("/admin") || p === "/playbook/gop-cau-chuyen" || p === "/nhan-xet-deck";

export default function App() {
  const { pathname } = useLocation();
  useEffect(() => {
    khoiDongDoLuong();
    // Bấm liên kết ra website khác (trang quỹ, nguồn trích dẫn…): chỉ ghi tên miền.
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a) return;
      let url;
      try { url = new URL(a.href, window.location.href); } catch { return; }
      if (!/^https?:$/.test(url.protocol) || url.hostname === window.location.hostname) return;
      track("outbound_click", { target: url.hostname.replace(/^www\./, "") });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  useEffect(() => {
    // Liên kết có #neo (ví dụ /ban-chuan-bi#tai-khoan) cuộn tới đúng khối sau
    // khi trang dựng xong; còn lại về đầu trang như trước.
    const neo = window.location.hash.slice(1);
    if (neo) {
      setTimeout(() => document.getElementById(neo)?.scrollIntoView(), 80);
    } else {
      window.scrollTo(0, 0);
    }
    const meta = metaFor(pathname, window.location.origin);
    document.title = meta.title;
    const description = document.querySelector('meta[name="description"]');
    if (description) description.setAttribute('content', meta.description);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', meta.canonical);
    // Thẻ robots phải đi theo trang. Trước đây chỉ được ghi lúc prerender, nên
    // rời /ban-chuan-bi sang trang khác thì noindex vẫn nằm lại trong DOM.
    let robots = document.querySelector('meta[name="robots"]');
    if (meta.noindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex');
    } else if (robots) {
      robots.remove();
    }
    ghiLuotXemTrang(pathname);
  }, [pathname]);
  return (
    <>
      <a className="skip-link" href="#noi-dung">
        Đến nội dung chính
      </a>
      <DauTrang />
      <main id="noi-dung" className="vo" tabIndex="-1" data-clarity-mask={laTrangCanChe(pathname) ? "True" : undefined}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/bat-dau" element={<BatDau />} />
          <Route path="/lo-trinh" element={<LoTrinh />} />
          <Route path="/cong-cu" element={<Directory />} />
          <Route path="/cong-cu/:slug" element={<ToolPage />} />
          <Route path="/nguon-von" element={<Funding />} />
          <Route path="/nguon-von/:slug" element={<FundDetail />} />
          <Route path="/chuong-trinh" element={<Programs />} />
          <Route path="/playbook" element={<Playbook />} />
          <Route path="/playbook/chu-de/:cat" element={<Playbook />} />
          <Route path="/playbook/gop-cau-chuyen" element={<StorySubmit />} />
          <Route path="/playbook/cam-nang" element={<Playbook />} />
          <Route path="/playbook/cau-chuyen" element={<Playbook />} />
          <Route path="/playbook/kinh-nghiem" element={<Playbook />} />
          <Route path="/playbook/nhin-lai" element={<Playbook />} />
          <Route path="/playbook/chinh-sach" element={<Playbook />} />
          <Route path="/mau" element={<TemplateList />} />
          <Route path="/mau/:slug" element={<TemplateDetail />} />
          <Route path="/playbook/:slug" element={<PlaybookArticle />} />
          <Route path="/admin/playbook" element={<PlaybookAdmin />} />
          <Route path="/admin/mau" element={<TemplateAdmin />} />
          <Route path="/admin/chuong-trinh" element={<ProgramAdmin />} />
          <Route path="/admin/nguon-von" element={<FundAdmin />} />
          <Route path="/admin/thong-ke" element={<StatsAdmin />} />
          <Route path="/admin/nhan-xet-deck" element={<NhanXetDeckAdmin />} />
          <Route path="/admin/spotlight" element={<SpotlightAdmin />} />
          <Route path="/admin/video" element={<VideoAdmin />} />
          <Route path="/video" element={<VideoLibrary />} />
          <Route path="/nhan-xet-deck" element={<NhanXetDeck />} />
          <Route path="/ban-chuan-bi" element={<Workspace />} />
          <Route path="/ban-chuan-bi/:muc" element={<Workspace />} />
          <Route path="/thuat-ngu" element={<Glossary />} />
          <Route path="/thuat-ngu/:slug" element={<GlossaryTerm />} />
          <Route path="/gioi-thieu" element={<About />} />
          <Route path="/huong-dan/goi-von-lan-dau" element={<Guide />} />
          {/* Mới có một bài, nên /huong-dan chuyển thẳng vào bài đó thay vì
              dựng một trang danh sách mỏng. Khi có nhiều bài thì thay bằng
              trang danh sách thật. */}
          <Route
            path="/huong-dan"
            element={<Navigate to="/huong-dan/goi-von-lan-dau" replace />}
          />
          <Route path="/quyen-rieng-tu" element={<Legal />} />
          <Route path="/dieu-khoan" element={<Legal />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <footer className="chan-trang">
        <div className="vo footer-grid">
          <div>
            <Link className="hieu" to="/">
              fundraising.vn
            </Link>
            {/* Thay cho khối "Con số chỉ hữu ích khi bạn hiểu nó" từng ở trang chủ. */}
            <p>
              <Link className="footer-tagline" to="/gioi-thieu" title="Cách chúng tôi làm">
                Hiểu con số. Chuẩn bị hồ sơ. Tìm bước tiếp theo. →
              </Link>
            </p>
            <small>
              Kết quả để tham khảo, không đảm bảo gọi vốn thành công.
            </small>
          </div>
          <div className="footer-links">
            <Link to="/lo-trinh">Lộ trình gọi vốn 5 trạm</Link>
            <Link to="/huong-dan/goi-von-lan-dau">Hướng dẫn gọi vốn lần đầu</Link>
            <Link to="/playbook">Playbook</Link>
            <Link to="/thuat-ngu">Từ điển thuật ngữ</Link>
            <Link to="/video">Video ngắn</Link>
            <Link to="/cong-cu">Công cụ</Link>
            <Link to="/nguon-von">Nguồn vốn</Link>
            <Link to="/chuong-trinh">Chương trình hỗ trợ</Link>
            <Link to="/nhan-xet-deck">Nhận xét pitch deck</Link>
            <Link to="/mau">Mẫu Excel</Link>
            <Link to="/gioi-thieu">Giới thiệu</Link>
            <Link to="/quyen-rieng-tu">Quyền riêng tư</Link>
            <Link to="/dieu-khoan">Điều khoản sử dụng</Link>
            <a href="mailto:xin-chao@fundraising.vn">Góp ý và báo lỗi</a>
            {DO_LUONG_BAT && (
              <button type="button" className="footer-link-button" onClick={moLaiHopChon}>
                Cài đặt cookie
              </button>
            )}
          </div>
        </div>
      </footer>
      <DongYCookie />
    </>
  );
}
